'use client';

import { useEffect, useRef } from 'react';
import logError from '../../utils/logError';
import { getAdaEmbed, isAdaRolledOut, startAdaEmbed } from './adaEmbed';
import { getUserMetadata, UserMetadata } from './adaRequests';
import useAdaToken from './useAdaToken';

export type Args = {
  fields?: UserMetadata;
  config: {
    handle: string | null;
    language: string | null;
  };
  experiment?: boolean;
};

const defaultHandle = 'alltrails';
const defaultLanguage = 'en';

function delay(milliseconds: number) {
  return new Promise(resolve => {
    setTimeout(resolve, milliseconds);
  });
}

async function refreshUserMetadata(attemptNumber = 1) {
  try {
    const userMetadata = await getUserMetadata();
    await getAdaEmbed()?.setMetaFields(userMetadata);
  } catch (error) {
    if (attemptNumber < 3) {
      logError(new Error('Ada user metadata refresh failed', { cause: error }), event => {
        event.addMetadata('ada', {
          retryCount: attemptNumber,
          action: 'refresh_user_metadata_failed'
        });
      });

      await delay(500);
      refreshUserMetadata(attemptNumber + 1);
    } else {
      logError(new Error('Ada user metadata refresh retries reached', { cause: error }), event => {
        event.addMetadata('ada', {
          retryCount: attemptNumber,
          action: 'refresh_user_metadata_retries_exceeded'
        });
      });
    }
  }
}

export default function AdaChatbot({ fields, config, experiment = false }: Args) {
  const adaHasBeenOpenedBefore = useRef(false);
  const { resetAdaTokenHandler } = useAdaToken();

  useEffect(() => {
    const { handle, language } = config;
    return startAdaEmbed({
      eventCallbacks: {
        // In the Ada configuration we fire this event after performing actions
        // that may have changed the metadata.
        refresh_user_data: () => {
          refreshUserMetadata();
        },
        'ada:toggle': isDrawerOpen => {
          if (isDrawerOpen) {
            // Upon re-opening Ada, we'll refresh the user metadata using the latest
            // data from the API.
            if (adaHasBeenOpenedBefore.current || !fields) {
              refreshUserMetadata();
            } else {
              getAdaEmbed()?.setMetaFields(fields);
            }

            adaHasBeenOpenedBefore.current = true;
          }
        },
        // This is called when the Ada chatbot is ready but not necessarily opened by a chatter.
        'ada:start': data => {
          const { isRolledOut } = data as { isRolledOut: boolean };
          const queryString = window.location.search;
          const urlParams = new URLSearchParams(queryString);
          const isAda = urlParams.get('ref') === 'ada';

          if (isRolledOut) {
            // This will initially set the alltrailsUserToken in Ada, and then refresh it periodically.
            // Doing it here instead of on open ensures that the token is set before the chatbot is opened
            // hopefully to avoid any issues with the token not being set in time.
            resetAdaTokenHandler();
            if (isAda) {
              // open chat window
              getAdaEmbed()?.toggle();
            }
          }
        }
      },
      handle: handle || defaultHandle,
      language: language || defaultLanguage
    });
  }, [fields, config, resetAdaTokenHandler]);

  useEffect(() => {
    // ada:start fires once per page, so a remount (e.g. client-side navigation) resumes the token refresh here.
    if (isAdaRolledOut()) {
      resetAdaTokenHandler();
    }
  }, [resetAdaTokenHandler]);

  useEffect(() => {
    // Hides the Ada launcher button; see globals.scss.
    document.body.classList.toggle('ada-experiment', experiment);

    return () => {
      document.body.classList.remove('ada-experiment');
    };
  }, [experiment]);

  return null;
}
